import { point, buffer, booleanIntersects, distance, lineString, nearestPointOnLine, booleanPointInPolygon } from '@turf/turf';

// Cache for fetched GeoJSON data from S3
const geojsonCache = new Map();

/**
 * Pre-load and cache GeoJSON data from S3
 * Should be called once on map load to avoid fetching on every proximity check
 */
export async function preloadGeoJSONCache() {
    const s3Sources = [
        { layerId: "tfr", url: "https://flyzonemediabuckets.s3.us-east-1.amazonaws.com/tfr.geojson" },
        { layerId: "seams", url: "https://flyzonemediabuckets.s3.us-east-1.amazonaws.com/laanc-seams.geojson" },
        { layerId: "b4ufly-boundary-airspace", url: "https://flyzonemediabuckets.s3.us-east-1.amazonaws.com/flyzone-b4ufly-part93-sata.geojson" },
        { layerId: "b4ufly-fria", url: "https://flyzonemediabuckets.s3.us-east-1.amazonaws.com/flyzone-b4ufly-fria.geojson" },
        { layerId: "b4ufly-recreational", url: "https://flyzonemediabuckets.s3.us-east-1.amazonaws.com/flyzone-b4ufly-recreational.geojson" },
        { layerId: "b4ufly-mtrs", url: "https://flyzonemediabuckets.s3.us-east-1.amazonaws.com/flyzone-b4ufly-mtrs.geojson" }
    ];

    // console.log('[GeoJSON Cache] Starting preload of', s3Sources.length, 'layers');

    const promises = s3Sources.map(async (source) => {
        if (!geojsonCache.has(source.url)) {
            try {
                // console.log(`[GeoJSON Cache] Preloading: ${source.layerId}`);
                const res = await fetch(source.url);
                const geojson = await res.json();
                geojsonCache.set(source.url, geojson);
                // console.log(`[GeoJSON Cache] ✓ Cached: ${source.layerId}`);
            } catch (error) {
                // console.error(`[GeoJSON Cache] ✗ Failed to cache ${source.layerId}:`, error);
            }
        }
    });

    await Promise.all(promises);
    // console.log(`[GeoJSON Cache] Preload complete. Cache size: ${geojsonCache.size}`);
}

/**
 * Find all layers within 2NM of a selected location using Mapbox Tilequery API and map sources
 * Calculates distance from pin to each layer
 *
 * @param {Object} pinLngLat - The selected location {lng, lat}
 * @param {string} mapboxAccessToken - Mapbox access token
 * @param {Object} operationArea - GeoJSON geometry of the operation area (circle or polygon)
 * @param {Object} mapRef - Mapbox map reference (optional, for querying sources directly)
 * @returns {Promise<Array>} - Array of features within 2NM with distance and intersection status
 */
export async function findLayersWithin2NM(pinLngLat, mapboxAccessToken, operationArea = null, mapRef = null) {
    // console.log('[2NM Filter] Starting with params:', {
    //     pinLngLat,
    //     hasAccessToken: !!mapboxAccessToken,
    //     hasOperationArea: !!operationArea,
    //     env: process.env.REACT_APP_ENV
    // });

    const pin = point([pinLngLat.lng, pinLngLat.lat]);
    const buffer2nm = buffer(pin, 2, { units: "nauticalmiles" });

    const results = [];

    // Determine environment for layers with qa variants
    const isQAorDev = ['qa', 'dev'].includes(process.env.REACT_APP_ENV);
    const uasfmLayer = isQAorDev ? 'qa-laanc-uasfm' : 'laanc-uasfm';
    const pnsufrLayer = isQAorDev ? 'qa-laanc-pnsufr' : 'laanc-pnsufr';

    // console.log('[2NM Filter] Using layer names:', { uasfmLayer, pnsufrLayer });

    // 1. Query Mapbox composite tileset using Tilequery API
    // Note: PNSUFR has environment-specific naming, others are the same across environments
    // Excluded layers:
    // - UASFM: handled by business logic
    // - Class airspaces (B/C/D/E): handled by Operational Awareness section
    const tilesets = [
        `thilankadamith.laanc-specialuse`,
        `thilankadamith.laanc-dc`,
        `thilankadamith.laanc-nsufr`,
        `thilankadamith.${pnsufrLayer}`,
        `thilankadamith.laanc-b4ufly-fish-wildlife`,
        `thilankadamith.laanc-b4ufly-wilderness-forest`,
        `thilankadamith.laanc-b4ufly-wilderness-areas`,
        `thilankadamith.laanc-b4ufly-national-parks`
    ];

    try {
        const tilequeryUrl =
            `https://api.mapbox.com/v4/${tilesets.join(",")}/tilequery/${pinLngLat.lng},${pinLngLat.lat}.json` +
            `?radius=${2 * 1852}` + // 2 NM in meters (1 NM = 1852 meters)
            `&limit=50` +
            `&dedupe=true` +
            `&access_token=${mapboxAccessToken}`;

        // console.log('[2NM Filter] Tilequery URL:', tilequeryUrl);

        const tilequeryRes = await fetch(tilequeryUrl);
        const tilequeryData = await tilequeryRes.json();

        // console.log('[2NM Filter] Tilequery response:', {
        //     featureCount: tilequeryData.features?.length || 0,
        //     hasError: !!tilequeryData.error
        // });

        if (tilequeryData.error) {
            // console.error('[2NM Filter] Tilequery API error:', tilequeryData.error);
        }

        for (const feature of tilequeryData.features || []) {
            // Verify intersection with 2NM buffer
            if (feature.geometry && booleanIntersects(buffer2nm, feature)) {
                // Calculate distance from pin to layer
                const distanceNM = calculateDistanceToFeature(pin, feature);

                // Check if it intersects with operation area
                const intersectsOperation = operationArea
                    ? booleanIntersects(operationArea, feature)
                    : false;

                // console.log('[2NM Filter] Found composite layer:', {
                //     layerId: feature.properties.tilequery?.layer || "unknown",
                //     distanceNM,
                //     intersectsOperation,
                //     properties: feature.properties
                // });

                results.push({
                    source: "composite",
                    layerId: feature.properties.tilequery?.layer || "unknown",
                    properties: feature.properties,
                    geometry: feature.geometry,
                    distanceNM: distanceNM,
                    intersectsOperation: intersectsOperation,
                    feature: feature
                });
            }
        }

        // console.log(`[2NM Filter] Tilequery API returned ${tilequeryData.features?.length || 0} features within 2NM`);
    } catch (error) {
        // console.error("[2NM Filter] Error querying Mapbox tilesets:", error);
    }

    // 2. Query S3-hosted GeoJSON layers
    // If mapRef is provided, use cached sources from the map; otherwise fetch from S3
    const s3Sources = [
        { source: "laanc-tfr", layerId: "tfr", url: "https://flyzonemediabuckets.s3.us-east-1.amazonaws.com/tfr.geojson" },
        { source: "laanc-seams", layerId: "seams", url: "https://flyzonemediabuckets.s3.us-east-1.amazonaws.com/laanc-seams.geojson" },
        { source: "laanc-b4ufly-boundary-airspace", layerId: "b4ufly-boundary-airspace", url: "https://flyzonemediabuckets.s3.us-east-1.amazonaws.com/flyzone-b4ufly-part93-sata.geojson" },
        { source: "laanc-b4ufly-faa-ria", layerId: "b4ufly-fria", url: "https://flyzonemediabuckets.s3.us-east-1.amazonaws.com/flyzone-b4ufly-fria.geojson" },
        { source: "laanc-b4ufly-recreational", layerId: "b4ufly-recreational", url: "https://flyzonemediabuckets.s3.us-east-1.amazonaws.com/flyzone-b4ufly-recreational.geojson" },
        { source: "laanc-b4ufly-mtrs", layerId: "b4ufly-mtrs", url: "https://flyzonemediabuckets.s3.us-east-1.amazonaws.com/flyzone-b4ufly-mtrs.geojson" }
    ];

    for (const sourceConfig of s3Sources) {
        try {
            let geojson = null;

            // Try to get data from map source first (cached)
            if (mapRef && mapRef.getSource(sourceConfig.source)) {
                // console.log(`[2NM Filter] Using cached source: ${sourceConfig.layerId}`);
                const source = mapRef.getSource(sourceConfig.source);
                // Check if _data is actual GeoJSON (object with type property) or just a URL string
                if (source._data && typeof source._data === 'object' && source._data.type) {
                    geojson = source._data;
                }
            }

            // If not available from map, check cache or fetch from S3
            if (!geojson) {
                // Check cache first
                if (geojsonCache.has(sourceConfig.url)) {
                    // console.log(`[2NM Filter] ✓ HIT - Using cached GeoJSON: ${sourceConfig.layerId} (cache size: ${geojsonCache.size})`);
                    geojson = geojsonCache.get(sourceConfig.url);
                } else {
                    // console.log(`[2NM Filter] ✗ MISS - Fetching S3 layer: ${sourceConfig.layerId} from ${sourceConfig.url}`);
                    const res = await fetch(sourceConfig.url);
                    geojson = await res.json();
                    // Store in cache
                    geojsonCache.set(sourceConfig.url, geojson);
                    // console.log(`[2NM Filter] ✓ Cached GeoJSON for: ${sourceConfig.layerId} (cache size: ${geojsonCache.size})`);
                }
            } else {
                // console.log(`[2NM Filter] ✓ Using GeoJSON from map source: ${sourceConfig.layerId}`);
            }

            const features = geojson.type === "FeatureCollection"
                ? geojson.features
                : [geojson];

            // console.log(`[2NM Filter] ${sourceConfig.layerId}: Total features in GeoJSON: ${features.length}`);

            let matchCount = 0;
            // console.log(`[2NM Filter] Processing ${features.length} features for layerId: "${sourceConfig.layerId}" (source: "${sourceConfig.source}")`);

            for (const feature of features) {
                // console.log(`[2NM Filter] Checking feature, layerId match: seams=${sourceConfig.layerId === 'seams'}, tfr=${sourceConfig.layerId === 'tfr'}, hasGeometry=${!!feature?.geometry}`);

                if (sourceConfig.layerId === 'tfr') {
                    // console.log(`[2NM Filter] TFR feature object:`, feature);
                    // console.log(`[2NM Filter] TFR feature keys:`, Object.keys(feature));
                }

                // SEAMS-specific logic: calculate distance to circle boundary
                if (sourceConfig.layerId === 'seams' && feature?.geometry) {
                    const distanceNM = calculateDistanceToFeature(pin, feature);

                    if (distanceNM <= 2) {
                        matchCount++;

                        // Check if it intersects with operation area
                        const intersectsOperation = operationArea
                            ? booleanIntersects(operationArea, feature)
                            : false;

                        // console.log(`[2NM Filter] Found S3 layer match:`, {
                        //     layerId: sourceConfig.layerId,
                        //     distanceNM,
                        //     intersectsOperation,
                        //     properties: feature.properties
                        // });

                        results.push({
                            source: sourceConfig.source,
                            layerId: sourceConfig.layerId,
                            url: sourceConfig.url,
                            properties: feature.properties,
                            geometry: feature.geometry,
                            distanceNM: distanceNM,
                            intersectsOperation: intersectsOperation,
                            feature: feature
                        });
                    }
                }
                // TFR-specific logic: calculate distance to polygon boundary
                else if (sourceConfig.layerId === 'tfr' && feature?.geometry) {
                    // console.log(`[2NM Filter] TFR block entered, feature:`, feature);
                    const distanceNM = calculateDistanceToFeature(pin, feature);
                    // console.log(`[2NM Filter] TFR distance check:`, {
                    //     distanceNM,
                    //     geometryType: feature.geometry.type,
                    //     isWithin2NM: distanceNM <= 2,
                    //     properties: feature.properties
                    // });

                    if (distanceNM <= 2) {
                        matchCount++;

                        // Check if it intersects with operation area
                        const intersectsOperation = operationArea
                            ? booleanIntersects(operationArea, feature)
                            : false;

                        // console.log(`[2NM Filter] Found S3 layer match:`, {
                        //     layerId: sourceConfig.layerId,
                        //     distanceNM,
                        //     intersectsOperation,
                        //     properties: feature.properties
                        // });

                        results.push({
                            source: sourceConfig.source,
                            layerId: sourceConfig.layerId,
                            url: sourceConfig.url,
                            properties: feature.properties,
                            geometry: feature.geometry,
                            distanceNM: distanceNM,
                            intersectsOperation: intersectsOperation,
                            feature: feature
                        });
                    }
                } else if (feature?.geometry && booleanIntersects(buffer2nm, feature)) {
                    // Original logic for all other layers
                    matchCount++;
                    // Calculate distance from pin to layer
                    const distanceNM = calculateDistanceToFeature(pin, feature);

                    // Check if it intersects with operation area
                    const intersectsOperation = operationArea
                        ? booleanIntersects(operationArea, feature)
                        : false;

                    // console.log(`[2NM Filter] Found S3 layer match:`, {
                    //     layerId: sourceConfig.layerId,
                    //     distanceNM,
                    //     intersectsOperation,
                    //     properties: feature.properties
                    // });

                    results.push({
                        source: sourceConfig.source,
                        layerId: sourceConfig.layerId,
                        url: sourceConfig.url,
                        properties: feature.properties,
                        geometry: feature.geometry,
                        distanceNM: distanceNM,
                        intersectsOperation: intersectsOperation,
                        feature: feature
                    });
                }
            }

            // console.log(`[2NM Filter] Found ${matchCount} features in ${sourceConfig.layerId} within 2NM`);
        } catch (error) {
            // console.error(`[2NM Filter] Error processing ${sourceConfig.layerId}:`, error);
        }
    }

    // Deduplicate results based on unique identifiers
    const seen = new Set();
    const deduplicatedResults = [];

    for (const result of results) {
        const props = result.properties || {};

        // Create a unique key based on available identifiers
        const uniqueKey =
            props.GLOBAL_ID ||
            props.GlobalID ||
            props.OBJECTID ||
            props.ObjectId ||
            props.notam_id ||
            props.GAME_DETAIL_ID ||
            props.refNumber ||
            props.id ||
            // Fallback: create a key from coordinates if no identifier exists
            `${result.layerId}_${JSON.stringify(result.geometry?.coordinates?.[0]?.[0] || '')}`;

        if (!seen.has(uniqueKey)) {
            seen.add(uniqueKey);
            deduplicatedResults.push(result);
        } else {
            // console.log(`[2NM Filter] Skipping duplicate:`, { uniqueKey, layerId: result.layerId });
        }
    }

    // 3. Check for DC SFRA (virtual layer)
    // SFRA is 15-34.5234 miles from Washington, D.C. center
    // Reference point: Lat. 38°51′34″ N., Long. 077°02′11″ W
    const dcCoords = [-77.0364, 38.8594]; // Washington, D.C. center coordinates
    const dcPoint = point(dcCoords);
    const distanceToDC = distance(pin, dcPoint, { units: 'miles' });

    // Check if within SFRA (15-34.5234 miles) OR within 2NM of SFRA boundaries
    const sfraNM = 2; // 2 nautical miles = ~2.3 statute miles
    const sfraTolerance = sfraNM * 1.15078; // Convert 2NM to statute miles for comparison
    const innerBoundary = 15.0;
    const outerBoundary = 34.5234;

    // Check if pin is:
    // 1. Inside SFRA (15-34.5234 miles), OR
    // 2. Within 2NM of inner boundary (13-15 miles), OR
    // 3. Within 2NM of outer boundary (34.5234-36.8 miles)
    const isInsideSFRA = distanceToDC >= innerBoundary && distanceToDC <= outerBoundary;
    const isNearInnerBoundary = distanceToDC >= (innerBoundary - sfraTolerance) && distanceToDC < innerBoundary;
    const isNearOuterBoundary = distanceToDC > outerBoundary && distanceToDC <= (outerBoundary + sfraTolerance);
    let isNearSFRA = isInsideSFRA || isNearInnerBoundary || isNearOuterBoundary;

    // Also check if any part of the operation area is within 2NM of SFRA boundaries
    if (!isNearSFRA && operationArea?.geometry?.coordinates) {
        const points = operationArea.geometry.type === 'Polygon'
            ? operationArea.geometry.coordinates[0]
            : operationArea.geometry.coordinates;

        isNearSFRA = points.some(coord => {
            const coordDistToDC = distance(point(coord), dcPoint, { units: 'miles' });
            const coordIsInsideSFRA = coordDistToDC >= innerBoundary && coordDistToDC <= outerBoundary;
            const coordIsNearInner = coordDistToDC >= (innerBoundary - sfraTolerance) && coordDistToDC < innerBoundary;
            const coordIsNearOuter = coordDistToDC > outerBoundary && coordDistToDC <= (outerBoundary + sfraTolerance);
            return coordIsInsideSFRA || coordIsNearInner || coordIsNearOuter;
        });
    }

    if (isNearSFRA) {
        // Calculate distance to nearest SFRA boundary
        let distanceToBoundary;
        if (isInsideSFRA) {
            // Inside SFRA: distance to nearest boundary (inner or outer)
            const distToInner = Math.abs(distanceToDC - innerBoundary);
            const distToOuter = Math.abs(distanceToDC - outerBoundary);
            distanceToBoundary = Math.min(distToInner, distToOuter);
        } else if (isNearInnerBoundary) {
            // Near inner boundary: distance to 15-mile boundary
            distanceToBoundary = Math.abs(innerBoundary - distanceToDC);
        } else {
            // Near outer boundary: distance to 30-mile boundary
            distanceToBoundary = Math.abs(distanceToDC - outerBoundary);
        }

        const distanceNM = distanceToBoundary * 0.868976; // Convert statute miles to nautical miles

        // Check if operation area intersects with SFRA
        let intersectsOperation = false;
        if (operationArea?.geometry?.coordinates) {
            const points = operationArea.geometry.type === 'Polygon'
                ? operationArea.geometry.coordinates[0]
                : operationArea.geometry.coordinates;

            intersectsOperation = points.some(coord => {
                const distMiles = distance(point(coord), dcPoint, { units: 'miles' });
                return distMiles >= innerBoundary && distMiles <= outerBoundary;
            });
        }

        deduplicatedResults.push({
            source: "virtual",
            layerId: "dc-sfra",
            properties: {
                name: "DC Special Flight Rules Area",
                description: "15-34.5234 mile radius from Washington, D.C."
            },
            geometry: null, // Virtual layer, no geometry
            distanceNM: parseFloat(distanceNM.toFixed(2)),
            intersectsOperation: intersectsOperation,
            feature: null
        });
    }

    // console.log(`[2NM Filter] FINAL RESULTS: Found ${deduplicatedResults.length} unique layers within 2NM (${results.length - deduplicatedResults.length} duplicates removed)`);
    // console.log(`[2NM Filter] Results breakdown:`, {
    //     total: deduplicatedResults.length,
    //     intersecting: deduplicatedResults.filter(r => r.intersectsOperation).length,
    //     nonIntersecting: deduplicatedResults.filter(r => !r.intersectsOperation).length,
    //     bySource: {
    //         composite: deduplicatedResults.filter(r => r.source === 'composite').length,
    //         s3: deduplicatedResults.filter(r => r.source !== 'composite').length
    //     }
    // });
    return deduplicatedResults;
}

/**
 * Calculate distance from a point to the nearest point on a feature
 * @param {Object} fromPoint - Turf point
 * @param {Object} feature - GeoJSON feature
 * @returns {number} - Distance in nautical miles
 */
function calculateDistanceToFeature(fromPoint, feature) {
    try {
        let distKm;

        // console.log('[calculateDistanceToFeature] Input:', {
        //     fromPointCoords: fromPoint.geometry.coordinates,
        //     featureType: feature.geometry.type,
        //     featureCoords: feature.geometry.coordinates
        // });

        if (feature.geometry.type === 'Point') {
            const targetPoint = point(feature.geometry.coordinates);
            distKm = distance(fromPoint, targetPoint, { units: 'kilometers' });
        } else if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
            // For polygons (including SEAMS circles and TFRs), calculate distance to the boundary
            // Check if point is inside the polygon
            const isInside = booleanPointInPolygon(fromPoint, feature);

            // Get polygon coordinates
            const coords = feature.geometry.type === 'Polygon'
                ? feature.geometry.coordinates[0]
                : feature.geometry.coordinates[0][0];

            // console.log('[calculateDistanceToFeature] Polygon processing:', {
            //     isInside,
            //     coordsLength: coords.length,
            //     firstCoord: coords[0]
            // });

            const line = lineString(coords);
            const nearest = nearestPointOnLine(line, fromPoint);
            distKm = distance(fromPoint, nearest, { units: 'kilometers' });

            // console.log('[calculateDistanceToFeature] Distance calculated:', {
            //     distKm,
            //     nearestPoint: nearest.geometry.coordinates
            // });
        } else if (feature.geometry.type === 'LineString') {
            const line = lineString(feature.geometry.coordinates);
            const nearest = nearestPointOnLine(line, fromPoint);
            distKm = distance(fromPoint, nearest, { units: 'kilometers' });
        } else {
            return 0;
        }

        const distNM = distKm * 0.539957; // Convert km to nautical miles
        // console.log('[calculateDistanceToFeature] Result:', { distKm, distNM });
        return parseFloat(distNM.toFixed(2));
    } catch (error) {
        // console.error("Error calculating distance:", error);
        return 0;
    }
}

/**
 * Get only layers that are within 2NM but do NOT intersect with operation area
 * Groups them by layer type for easy lookup
 *
 * @param {Array} proximityLayers - All layers found within 2NM
 * @returns {Object} - Object keyed by layerId with arrays of non-intersecting layers
 */
export function getNonIntersectingLayers(proximityLayers) {
    const nonIntersecting = proximityLayers.filter(layer => !layer.intersectsOperation);

    // Group by layerId for easy lookup
    const grouped = {};
    nonIntersecting.forEach(layer => {
        const key = layer.layerId.toLowerCase();
        if (!grouped[key]) {
            grouped[key] = [];
        }
        grouped[key].push(layer);
    });

    // console.log(`Found ${nonIntersecting.length} layers within 2NM that don't intersect operation area`);
    return grouped;
}
